import { endOfMonth, isSameDay, isSameMonth, parseISO, startOfMonth } from 'date-fns';

import type { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import {
    BILLABLE_CASE_STATUSES,
    BILLING_COUNTRIES,
    DEFAULT_BILLING_INSTALLMENTS,
} from 'data/constants/serviceProviderBilling';
import type {
    AssessmentRevenuePoint,
    BillingInstallment,
    BillingModalData,
    BillingRevenuePoint,
    BillingRevenueResponse,
    BillingRule,
    BillingRuleFormValues,
    CumulativeRevenuePoint,
    CumulativeRevenueSeries,
    ServiceBilling,
    ServiceCountryCode,
    ServiceProviderBillingConfig,
    ServiceRecord,
} from 'types';
import { localizedServiceName } from 'utils/servicesDocuments';

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

const statusRank = (status: ClientApplicationStatus): number =>
    BILLABLE_CASE_STATUSES.indexOf(status);

const earliestStatusRank = (statuses: ClientApplicationStatus[]): number =>
    statuses.length ? Math.min(...statuses.map(statusRank)) : Number.MAX_SAFE_INTEGER;

export const getCountryCurrency = (countryCode: ServiceCountryCode): string =>
    BILLING_COUNTRIES.find((country) => country.code === countryCode)?.currency ?? 'USD';

export const getInstallmentsTotal = (installments: { percentage: number | string }[]): number =>
    roundToCents(
        installments.reduce(
            (total, installment) => total + (Number(installment.percentage) || 0),
            0
        )
    );

export const getRemainingPercentage = (installments: { percentage: number | string }[]): number =>
    Math.max(0, roundToCents(100 - getInstallmentsTotal(installments)));

export const getInstallmentAmount = (price: number, percentage: number): number =>
    roundToCents((price * percentage) / 100);

/** Orders statuses inside each installment, then installments by their earliest status. */
export const sortInstallments = (installments: BillingInstallment[]): BillingInstallment[] =>
    installments
        .map((installment) => ({
            ...installment,
            statuses: [...installment.statuses].sort((a, b) => statusRank(a) - statusRank(b)),
        }))
        .sort((a, b) => earliestStatusRank(a.statuses) - earliestStatusRank(b.statuses));

export const toBillingRule = (values: BillingRuleFormValues): BillingRule => ({
    price: Number(values.price),
    currency: values.currency,
    installments: sortInstallments(
        values.installments.map((installment) => ({
            statuses: installment.statuses,
            percentage: Number(installment.percentage),
        }))
    ),
});

export const toBillingRuleFormValues = (
    rule: BillingRule | null,
    countryCode: ServiceCountryCode
): BillingRuleFormValues => ({
    price: rule ? String(rule.price) : '',
    currency: rule?.currency ?? getCountryCurrency(countryCode),
    installments: (rule?.installments ?? DEFAULT_BILLING_INSTALLMENTS).map((installment) => ({
        statuses: [...installment.statuses],
        percentage: String(installment.percentage),
    })),
});

export const getServiceBilling = (
    serviceId: number,
    config: ServiceProviderBillingConfig | null
): ServiceBilling => {
    const override = config?.overrides.find((item) => item.service_id === serviceId);
    if (override) {
        return { rule: override, source: 'custom' };
    }
    if (config?.default_rule) {
        return { rule: config.default_rule, source: 'default' };
    }
    return { rule: null, source: 'none' };
};

/** A single service opens with the rule it currently uses; bulk edits start from the default. */
export const getEditableRule = (
    data: BillingModalData,
    config: ServiceProviderBillingConfig | null
): BillingRule | null =>
    data.target === 'services' && data.serviceIds.length === 1
        ? getServiceBilling(data.serviceIds[0], config).rule
        : (config?.default_rule ?? null);

export const withDefaultRule = (
    config: ServiceProviderBillingConfig,
    rule: BillingRule | null
): ServiceProviderBillingConfig => ({ ...config, default_rule: rule });

/** Gives each service its own copy of `rule`, replacing any override it already had. */
export const withServiceOverrides = (
    config: ServiceProviderBillingConfig,
    serviceIds: number[],
    rule: BillingRule
): ServiceProviderBillingConfig => ({
    ...config,
    overrides: [
        ...config.overrides.filter((override) => !serviceIds.includes(override.service_id)),
        ...serviceIds.map((service_id) => ({ ...rule, service_id })),
    ],
});

export const withoutServiceOverrides = (
    config: ServiceProviderBillingConfig,
    serviceIds: number[]
): ServiceProviderBillingConfig => ({
    ...config,
    overrides: config.overrides.filter((override) => !serviceIds.includes(override.service_id)),
});

export const countServicesOnDefault = (
    services: ServiceRecord[],
    config: ServiceProviderBillingConfig | null
): number => {
    if (!config?.default_rule) {
        return 0;
    }
    const overriddenIds = new Set(config.overrides.map((override) => override.service_id));
    return services.filter((service) => !overriddenIds.has(service.id)).length;
};

/** Running total of revenue due to Aliro for one currency, up to and including each UTC day. */
type RevenueAmountsPoint = Pick<BillingRevenuePoint, 'day' | 'amounts'> & {
    assessments?: number;
    cases?: number;
};

export const getCumulativeRevenue = (
    points: RevenueAmountsPoint[],
    currency: string
): CumulativeRevenuePoint[] => {
    let total = 0;

    return points.map((point) => {
        const revenue = point.amounts[currency] ?? 0;
        total = roundToCents(total + revenue);
        return {
            day: point.day,
            revenue,
            total,
        };
    });
};

/** Currency controls include both service and completed-assessment revenue. */
export const getRevenueCurrencies = (revenue: BillingRevenueResponse): string[] =>
    [...new Set([...revenue.currencies, ...revenue.assessmentRevenue.currencies])].sort();

interface MonthlyRevenuePoint {
    day: string;
    count: number;
    amounts: Record<string, number>;
}

const aggregateRevenueByMonth = (
    points: RevenueAmountsPoint[],
    getCount: (point: RevenueAmountsPoint) => number
): MonthlyRevenuePoint[] => {
    const months = new Map<string, MonthlyRevenuePoint>();

    points.forEach((point) => {
        const month = `${point.day.slice(0, 7)}-01`;
        const aggregate = months.get(month) ?? { day: month, count: 0, amounts: {} };
        aggregate.count += getCount(point);
        Object.entries(point.amounts).forEach(([currency, amount]) => {
            aggregate.amounts[currency] = roundToCents((aggregate.amounts[currency] ?? 0) + amount);
        });
        months.set(month, aggregate);
    });

    return [...months.values()].sort((a, b) => a.day.localeCompare(b.day));
};

export const getMonthlyBillingRevenue = (points: BillingRevenuePoint[]): BillingRevenuePoint[] =>
    aggregateRevenueByMonth(points, (point) => point.cases ?? 0).map(({ day, count, amounts }) => ({
        day,
        cases: count,
        amounts,
    }));

export const getMonthlyAssessmentRevenue = (
    points: AssessmentRevenuePoint[]
): AssessmentRevenuePoint[] =>
    aggregateRevenueByMonth(points, (point) => point.assessments ?? 0).map(
        ({ day, count, amounts }) => ({ day, assessments: count, amounts })
    );

export const getRevenueMonthQualifier = (
    month: string,
    rangeFrom: string,
    rangeTo: string,
    today = new Date()
): 'monthToDate' | 'partialMonth' | null => {
    const monthDate = parseISO(month);
    const fromDate = parseISO(rangeFrom);
    const toDate = parseISO(rangeTo);
    const startsPartwayThroughMonth =
        isSameMonth(monthDate, fromDate) && !isSameDay(fromDate, startOfMonth(fromDate));
    const endsPartwayThroughMonth =
        isSameMonth(monthDate, toDate) && !isSameDay(toDate, endOfMonth(toDate));

    if (endsPartwayThroughMonth && isSameDay(toDate, today)) {
        return 'monthToDate';
    }
    return startsPartwayThroughMonth || endsPartwayThroughMonth ? 'partialMonth' : null;
};

/** Keeps case and assessment revenue separate while calculating their cumulative total. */
export const getCumulativeRevenueSeries = (
    servicePoints: RevenueAmountsPoint[],
    assessmentPoints: RevenueAmountsPoint[],
    currency: string
): CumulativeRevenueSeries => {
    const servicesByDay = new Map(servicePoints.map((point) => [point.day, point]));
    const assessmentsByDay = new Map(assessmentPoints.map((point) => [point.day, point]));
    const days = [...new Set([...servicesByDay.keys(), ...assessmentsByDay.keys()])].sort();
    const series: CumulativeRevenueSeries = { services: [], assessments: [], total: [] };
    let servicesTotal = 0;
    let assessmentsTotal = 0;
    let total = 0;

    days.forEach((day) => {
        const servicesRevenue = servicesByDay.get(day)?.amounts[currency] ?? 0;
        const assessmentsRevenue = assessmentsByDay.get(day)?.amounts[currency] ?? 0;
        const totalRevenue = roundToCents(servicesRevenue + assessmentsRevenue);

        servicesTotal = roundToCents(servicesTotal + servicesRevenue);
        assessmentsTotal = roundToCents(assessmentsTotal + assessmentsRevenue);
        total = roundToCents(total + totalRevenue);

        series.services.push({ day, revenue: servicesRevenue, total: servicesTotal });
        series.assessments.push({ day, revenue: assessmentsRevenue, total: assessmentsTotal });
        series.total.push({ day, revenue: totalRevenue, total });
    });

    return series;
};

/** The API sorts by English name, so services are re-sorted by their name in the UI language. */
export const sortServicesByName = (
    services: ServiceRecord[],
    language: string | undefined
): ServiceRecord[] =>
    [...services].sort((a, b) =>
        localizedServiceName(a, language).localeCompare(localizedServiceName(b, language))
    );
